import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "wouter";
import { logoUrls } from "@/lib/objectStorage";
import { ThemeToggle } from "@/components/theme-toggle";

interface NavLink {
  href: string;
  label: string;
  isExternal?: boolean;
}

const navLinks: NavLink[] = [
  { href: "/", label: "Home", isExternal: true },
  { href: "/team", label: "Team", isExternal: true },
  { href: "/voices", label: "Voices", isExternal: true },
];

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [location] = useLocation();

  // Filter out the current page from navigation links
  const visibleNavLinks = navLinks.filter(link => link.href !== location);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/80 dark:bg-black/80 backdrop-blur-md shadow-md" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link href="/">
              <div className="flex items-center cursor-pointer">
                <>
                  <img src={logoUrls.darkMode} width="100" height="25" alt="Elevita AI Logo" className="hidden dark:block" />
                  <img src={logoUrls.lightMode} width="100" height="25" alt="Elevita AI Logo" className="block dark:hidden" />
                </>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {visibleNavLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <div className="text-black dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer">
                  {link.label}
                </div>
              </Link>
            ))}
            <ThemeToggle />
            <Button
              asChild
              className="bg-teal-500 text-white hover:bg-teal-600 transition-colors"
            >
              <Link href="/#contact">
                Contact
              </Link>
            </Button>
          </div>

          {/* Mobile Navigation Toggle */}
          <div className="md:hidden flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="text-black dark:text-white hover:text-teal-600 dark:hover:text-teal-400"
              onClick={toggleMenu}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 flex flex-col space-y-4 bg-white/90 dark:bg-black/90 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
            <div className="flex justify-end pb-2">
              <ThemeToggle />
            </div>
            {visibleNavLinks.map((link) => (
              <Link key={link.href} href={link.href}>
                <div 
                  className="text-black dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors py-2 cursor-pointer"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </div>
              </Link>
            ))}
            <Button
              asChild
              className="bg-teal-500 text-white hover:bg-teal-600 transition-colors w-full"
            >
              <Link href="/#contact">
                Contact
              </Link>
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
}
